import { useMutation, useQueryClient } from "@tanstack/react-query"
import { trpc } from "app/trpc"
import { PageType } from "db/enums"
import { useCallback, useState } from "react"
import startViewTransition from "../utils/startViewTransition"
import { useCurrentUser } from "./useCurrentUser"

type PopoverState = "OPENED" | "MINIMIZED" | "CLOSED"

export default function usePopoverSeen(pageType: PageType) {
  const queryClient = useQueryClient()
  const seenPopoverMutation = useMutation(
    trpc.onboarding.addSeenPopover.mutationOptions()
  )
  const [suppressedState, setSuppressedState] = useState<PopoverState | null>(
    null
  )
  const [transitionState, setTransitionState] = useState<PopoverState | null>(
    null
  )
  const currentUser = useCurrentUser()

  const toggle = useCallback(
    async (state: PopoverState) => {
      setSuppressedState(null) // Clear suppressed state when toggle is called
      await seenPopoverMutation.mutateAsync({ pageType, state })
      await queryClient.invalidateQueries({
        queryKey: trpc.users.getCurrentUser.queryKey(),
      })
      startViewTransition(() => {
        setTransitionState(state || "OPENED")
      })
    },
    [pageType, queryClient, seenPopoverMutation]
  )

  const suppress = useCallback(async (state: PopoverState) => {
    setSuppressedState(state)
  }, [])

  if (!currentUser)
    return {
      state: "CLOSED" as const,
      toggle: async (state: PopoverState) => {},
      suppress: async (state: PopoverState) => {},
    }

  // Filter seenPopovers by pageType client-side
  const seenPopover = currentUser.seenPopovers.find(
    (sp) => sp.pageType === pageType
  )

  // Use suppressed state if it exists, otherwise use the stored state
  const effectiveState =
    suppressedState || transitionState || seenPopover?.state || "OPENED"

  return {
    state: effectiveState,
    toggle,
    suppress,
  } as const
}
